import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useOrg } from "../org";
import { Field, REL, Select } from "./bits";

export const CaregiverDialog = ({ person, onClose, onSaved }) => {
  const { api } = useOrg();
  const [list, setList] = useState([]);
  useEffect(() => { api("/caregivers").then(setList).catch((e) => toast.error(e.message)); }, [api]);
  const pick = (id) => api(`/people/${person.id}`, { method: "PATCH", body: { caregiver_id: id } })
    .then(() => { toast.success(id ? `${list.find((c) => c.id === id)?.name} is now ${person.first_name}'s Assigned Care Leader` : "Assigned Care Leader removed"); onSaved(); })
    .catch((e) => toast.error(e.message));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-paper max-w-md" data-testid="caregiver-dialog">
        <DialogTitle className="t-name-lg">Choose {person.first_name}'s Assigned Care Leader</DialogTitle>
        <DialogDescription className="t-meta">One person owns the next step. Groups and other leaders can still help.</DialogDescription>
        <div className="rule-list border-y border-line mt-2">
          {list.map((c) => (
            <button key={c.id} onClick={() => pick(c.id)} className="w-full text-left py-4 flex justify-between gap-3" data-testid={`caregiver-option-${c.id}`}>
              <span className="t-name-sm">{c.name}</span>
              <span className={`t-meta ${c.caring_for >= c.comfortable_load ? "text-sun" : ""}`}>Caring for {c.caring_for}{person.caregiver?.id === c.id ? " · current" : ""}</span>
            </button>
          ))}
        </div>
        {person.caregiver && <button onClick={() => pick(null)} className="btn btn-quiet btn-sm" data-testid="caregiver-clear">Remove Assigned Care Leader</button>}
      </DialogContent>
    </Dialog>
  );
};

export const HouseholdDialog = ({ person, onClose, onSaved }) => {
  const { api } = useOrg();
  const [name, setName] = useState(person.household.name);
  const [people, setPeople] = useState([]);
  const [add, setAdd] = useState(null);
  const [rel, setRel] = useState("relative");
  useEffect(() => { api("/people").then(setPeople).catch(() => {}); }, [api]);
  const run = (p, msg) => p.then(() => { toast.success(msg); onSaved(); }).catch((e) => toast.error(e.message));
  const inHome = new Set([person.id, ...(person.household_members || []).map((m) => m.id)]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-paper max-w-md" data-testid="household-dialog">
        <DialogTitle className="t-name-lg">Manage household</DialogTitle>
        <DialogDescription className="t-meta">Changes apply to everyone in this household.</DialogDescription>
        <Field label="Household name">
          <div className="flex gap-2"><input value={name} onChange={(e) => setName(e.target.value)} className="field h-12 px-3" data-testid="household-name-input" />
            <button onClick={() => run(api(`/households/${person.household.id}`, { method: "PATCH", body: { name } }), "Household renamed")} className="btn btn-outline btn-sm" data-testid="household-rename-btn">Save</button></div>
        </Field>
        <div className="rule-list border-y border-line">
          {(person.household_members || []).map((m) => (
            <div key={m.id} className="py-3 flex justify-between items-center gap-3" data-testid={`household-member-${m.id}`}>
              <span className="t-body">{m.first_name} {m.last_name} <span className="t-meta">· {REL[m.relationship] || "Member"}</span></span>
              <button onClick={() => run(api(`/people/${m.id}`, { method: "PATCH", body: { household_id: null } }), "Removed from household")} className="btn btn-quiet btn-sm" data-testid={`household-remove-${m.id}`}>Remove</button>
            </div>
          ))}
        </div>
        <Field label="Add someone to this household">
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Select value={add} onChange={setAdd} testid="household-add-person"><option value="">Choose a person…</option>{people.filter((p) => !inHome.has(p.id)).map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}</Select>
            <Select value={rel} onChange={setRel} testid="household-add-relationship" className="!w-auto">{Object.entries(REL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
          </div>
        </Field>
        <button disabled={!add} onClick={() => run(api(`/people/${add}`, { method: "PATCH", body: { household_id: person.household.id, household_relationship: rel } }), "Added to household")} className="btn btn-primary w-full" data-testid="household-add-btn">Add to household</button>
      </DialogContent>
    </Dialog>
  );
};
